<template>
  <div class="flex w-full h-screen overflow-hidden bg-slate-100">
    <div class="flex-1 w-full overflow-hidden">
      <Topbar />

      <div class="mt-[64px] flex h-[calc(100vh-64px)] flex-col overflow-hidden p-6">
        <div class="mb-4 flex shrink-0 items-start justify-between gap-3">
          <div>
          <h2 class="flex items-center gap-2 text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">
            Витрины
          </h2>
          <p class="mt-1 text-sm text-slate-500 md:text-base">Управление витринами и составом каждой сборки</p>
          </div>
          <div class="flex gap-2">
            <Button label="Создать" icon="pi pi-plus" severity="success" class="!shadow-sm" @click="router.push('/create-showcase')" />
            <Button label="Обновить" icon="pi pi-refresh" severity="secondary" outlined class="!border-slate-200 !shadow-sm" @click="fetchData" />
          </div>
        </div>

        <Toast />

        <div class="min-h-0 flex-1 overflow-hidden rounded-2xl">
          <Card class="flex h-full min-h-0 flex-col overflow-hidden">
          <template #content>
            <div class="flex h-full min-h-0 flex-col">
              <div class="flex shrink-0 flex-col gap-4 px-4 py-2 lg:flex-row lg:items-center lg:justify-between">
                <IconField class="w-full lg:max-w-sm">
                  <InputIcon class="pi pi-search" />
                  <InputText
                    v-model.trim="showcaseQuery"
                    placeholder="Поиск по названию или штрихкоду"
                    class="w-full !rounded-xl !border-gray-200 !bg-white/95"
                  />
                </IconField>
              </div>

              <div class="min-h-0 flex-1 overflow-y-auto pr-1">
                <div v-if="loading" class="py-8 text-center text-slate-500">Загрузка...</div>
                <div v-else-if="!filteredShowcases.length" class="py-8 text-center text-slate-500">Ничего не найдено</div>

                <div v-else class="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                  <div
                    v-for="showcase in filteredShowcases"
                    :key="showcase.id"
                    class="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-md cursor-pointer"
                  >
                    <div class="absolute inset-x-0 top-0 z-10 flex items-start justify-between gap-2 p-2.5 bg-white/60">
                      <div class="min-w-0 rounded-xl bg-white/92 px-2.5 py-1.5 shadow-sm backdrop-blur-sm">
                        <p class="truncate text-sm font-semibold text-slate-900">{{ showcase.name }}</p>
                      </div>
                      <div class="max-w-[52%] rounded-xl bg-white/92 px-2.5 py-1.5 text-right shadow-sm backdrop-blur-sm">
                        <p class="text-[10px] uppercase tracking-wide text-slate-500">Штрихкод</p>
                        <p class="truncate text-xs font-semibold text-slate-900">{{ showcaseBarcode(showcase) }}</p>
                      </div>
                    </div>

                    <div
                      class="relative aspect-[5/4] overflow-hidden bg-slate-100"
                      :class="{ 'cursor-zoom-in': Boolean(showcase.image) }"
                      @click="openPreview(showcase)"
                    >
                      <button
                        v-if="showcase.image"
                        type="button"
                        class="block h-full w-full !cursor-pointer"
                        @click.stop="openPreview(showcase)"
                      >
                        <img
                          :src="showcase.image"
                          :alt="showcase.name"
                          class="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.04]"
                        >
                      </button>
                      <div
                        v-else
                        class="flex h-full w-full items-center justify-center bg-slate-50 text-sm text-slate-500"
                      >
                        Фото не загружено
                      </div>

                      <div class="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-slate-950/16 to-transparent" />
                      <div class="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-slate-950/10 to-transparent" />
                      <div class="pointer-events-none absolute inset-0 bg-slate-950/0 transition-colors duration-200 group-hover:bg-slate-950/10" />
                      <div class="pointer-events-none absolute inset-x-0 top-1/2 z-20 flex -translate-y-1/2 justify-center px-4 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                        <div class="pointer-events-auto flex items-center gap-2 rounded-xl border border-white/70 bg-white/92 p-1.5 shadow-lg backdrop-blur-sm">
                          <Button icon="pi pi-pencil" severity="success" size="small" rounded @click.stop="openEdit(showcase)" />
                          <Button icon="pi pi-trash" severity="danger" size="small" rounded @click.stop="deleteShowcase(showcase.id)" />
                        </div>
                      </div>
                    </div>

                    <div class="flex items-center justify-between gap-3 p-3">
                      <div>
                        <p class="text-[10px] uppercase tracking-wide text-slate-500">Кол-во</p>
                        <p class="text-sm font-semibold text-slate-900">{{ showcase.quantity ?? 0 }}</p>
                      </div>
                      <div class="text-right">
                        <p class="text-[10px] uppercase tracking-wide text-slate-500">Сумма</p>
                        <p class="text-sm font-semibold text-slate-900">{{ showcaseTotal(showcase) }} ₸</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          </Card>
        </div>
      </div>
    </div>
  </div>

  <Dialog v-model:visible="editDialog" modal header="Редактировать витрину" :style="{ width: 'min(96vw, 1120px)' }">
    <div class="grid grid-cols-1 gap-3 pt-2 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
      <FloatLabel variant="on">
        <InputText id="edit-name" v-model.trim="editForm.name" class="w-full" />
        <label for="edit-name">Название</label>
      </FloatLabel>

      <div class="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
        <FloatLabel variant="on">
          <InputNumber id="edit-rate" v-model="editForm.order_collection_rate" :min="0" class="w-full" />
          <label for="edit-rate">Тариф</label>
        </FloatLabel>

        <Button
          v-if="!editShowcaseBarcode"
          label="Сгенерировать штрихкод"
          icon="pi pi-barcode"
          severity="secondary"
          outlined
          :loading="generatingBarcode"
          @click="generateShowcaseBarcode"
        />
      </div>
    </div>

    <div class="mt-3 flex items-end justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
      <div class="min-w-0">
        <p class="text-[11px] uppercase tracking-wide text-slate-500">Штрихкод витрины</p>
        <p class="mt-1 truncate text-sm font-semibold text-slate-900">{{ editShowcaseBarcode || 'Не назначен' }}</p>
      </div>
      <Button
        label="Экспорт"
        icon="pi pi-file-pdf"
        severity="secondary"
        outlined
        :disabled="!editShowcaseBarcode"
        @click="exportShowcaseBarcodePdf"
      />
    </div>

    <div class="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1.15fr_0.85fr]">
      <div class="min-h-0 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div class="mb-3 flex items-center justify-between gap-3">
          <div>
            <p class="text-sm font-semibold text-slate-900">Позиции витрины</p>
            <p class="text-xs text-slate-500">Текущий состав и количество позиций</p>
          </div>
          <div class="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-right">
            <p class="text-[11px] uppercase tracking-wide text-emerald-700">Сумма</p>
            <p class="text-sm font-semibold text-emerald-800">{{ editShowcaseTotal }} ₸</p>
          </div>
        </div>

        <div v-if="!editForm.items.length" class="flex min-h-[18rem] items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white text-sm text-slate-500">
          Витрина пока не содержит позиций
        </div>

        <div v-else class="max-h-[28rem] space-y-2 overflow-auto pr-1">
          <div
            v-for="item in editForm.items"
            :key="`showcase-item-${item.id}-${item.store_id}`"
            class="rounded-2xl border border-slate-200 bg-white p-3"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <p class="truncate text-sm font-semibold text-slate-900">{{ item.name }}</p>
                <p class="mt-1 text-xs text-slate-500">
                  Остаток: {{ item.amount ?? 0 }} | Цена: {{ item.price ?? 0 }} ₸
                </p>
              </div>
              <Button icon="pi pi-trash" severity="danger" text size="small" @click="removeItemFromEdit(item.id)" />
            </div>

            <div class="mt-3 flex items-center justify-between gap-3">
              <div class="flex items-center gap-2">
                <Button icon="pi pi-minus" severity="secondary" text size="small" @click="decrementEditItem(item)" />
                <InputNumber
                  v-model="item.quantity"
                  :min="1"
                  :max="item.amount || 1"
                  inputClass="w-20 text-sm"
                  @update:modelValue="clampEditItemQuantity(item)"
                />
                <Button icon="pi pi-plus" severity="secondary" text size="small" @click="incrementEditItem(item)" />
              </div>
              <p class="text-sm font-semibold text-slate-900">
                {{ (Number(item.quantity || 0) * Number(item.price || 0)) }} ₸
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="min-h-0 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div class="mb-3 flex items-center gap-2">
          <Button
            label="Товары"
            size="small"
            :severity="editSideTab === 'items' ? 'success' : 'secondary'"
            :outlined="editSideTab !== 'items'"
            @click="editSideTab = 'items'"
          />
          <Button
            label="Фото витрины"
            size="small"
            :severity="editSideTab === 'image' ? 'success' : 'secondary'"
            :outlined="editSideTab !== 'image'"
            @click="editSideTab = 'image'"
          />
        </div>

        <template v-if="editSideTab === 'items'">
          <p class="text-sm font-semibold text-slate-900">Добавить товар</p>
          <p class="mt-1 text-xs text-slate-500">Поиск по доступным товарам склада</p>

          <InputText v-model.trim="editStoreQuery" placeholder="Поиск товара по названию или штрихкоду..." class="mt-3 w-full" />

          <div class="mt-3 max-h-[28rem] space-y-2 overflow-auto pr-1">
            <div
              v-for="store in filteredEditStoreItems"
              :key="`edit-${store.id}`"
              class="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-3"
            >
              <div class="min-w-0">
                <p class="truncate text-sm font-medium text-slate-900">{{ store.name }}</p>
                <p class="mt-1 text-xs text-slate-500">Штрихкод: {{ store.barcode || '—' }}</p>
                <p class="mt-1 text-xs text-slate-500">Цена: {{ store.price ?? 0 }} ₸ | Остаток: {{ store.amount ?? 0 }}</p>
              </div>
              <Button size="small" icon="pi pi-plus" severity="success" label="Добавить" @click="addStoreToEdit(store)" :disabled="Number(store.amount || 0) <= 0" />
            </div>
          </div>
        </template>

        <template v-else>
          <p class="text-sm font-semibold text-slate-900">Фото витрины</p>
          <p class="mt-1 text-xs text-slate-500">Добавление или замена изображения витрины</p>

          <div class="mt-4 flex items-center gap-3">
            <FileUpload
              mode="basic"
              accept="image/*"
              :maxFileSize="4096000"
              customUpload
              auto
              chooseLabel="Выбрать фото"
              chooseIcon="pi pi-image"
              :chooseButtonProps="{ severity: 'success' }"
              @select="onEditImageSelect"
            />
            <Button
              v-if="editImagePreview || editForm.image"
              label="Очистить"
              icon="pi pi-times"
              severity="danger"
              text
              @click="clearEditImage"
            />
          </div>

          <div v-if="editImagePreview || editForm.image" class="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <img
              :src="editImagePreview || editForm.image"
              :alt="editForm.name || 'Фото витрины'"
              class="h-64 w-full object-cover"
            >
          </div>
        </template>
      </div>
    </div>

    <template #footer>
      <Button label="Отмена" severity="secondary" outlined @click="editDialog = false" />
      <Button label="Сохранить" severity="success" icon="pi pi-check" :loading="updating" @click="updateShowcase" />
    </template>
  </Dialog>

  <Dialog v-model:visible="previewDialog" modal :dismissableMask="true" :draggable="false" :style="{ width: 'min(92vw, 960px)' }">
    <template #header>
      <div class="min-w-0">
        <p class="truncate text-base font-semibold text-slate-900">{{ previewImageAlt }}</p>
      </div>
    </template>

    <div class="overflow-hidden rounded-2xl bg-slate-100">
      <img
        v-if="previewImageSrc"
        :src="previewImageSrc"
        :alt="previewImageAlt"
        class="max-h-[75vh] w-full object-contain"
      >
    </div>
  </Dialog>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import axios from '../lib/axios'
import { useToast } from 'primevue/usetoast'
import Toast from 'primevue/toast'
import { exportBarcodePdfLabel } from '../utils/barcodePdf'

const toast = useToast()
const router = useRouter()

const loading = ref(false)
const updating = ref(false)
const generatingBarcode = ref(false)

const showcases = ref([])
const storeItems = ref([])
const editDialog = ref(false)
const editSideTab = ref('items')
const editStoreQuery = ref('')
const showcaseQuery = ref('')
const previewDialog = ref(false)
const previewImageSrc = ref('')
const previewImageAlt = ref('')
const editImageFile = ref(null)
const editImagePreview = ref('')
const editForm = ref({
  id: null,
  name: '',
  barcode: [],
  image: '',
  order_collection_rate: 0,
  items: []
})

const filteredEditStoreItems = computed(() => {
  const q = editStoreQuery.value.toLowerCase()
  if (!q) return storeItems.value
  return storeItems.value.filter((p) => {
    const nameMatch = (p.name || '').toLowerCase().includes(q)
    const barcodeMatch = String(p.barcode || '').toLowerCase().includes(q)
    return nameMatch || barcodeMatch
  })
})

const filteredShowcases = computed(() => {
  const q = showcaseQuery.value.trim().toLowerCase()
  if (!q) return showcases.value

  return showcases.value.filter((showcase) => {
    const name = String(showcase?.name || '').toLowerCase()
    const barcode = showcaseBarcode(showcase).toLowerCase()
    return name.includes(q) || barcode.includes(q)
  })
})

const showcaseTotal = (showcase) => {
  const products = (showcase.items || []).reduce((sum, item) => {
    return sum + (Number(item.quantity) || 0) * (Number(item.price) || 0)
  }, 0)

  return products + (Number(showcase.order_collection_rate) || 0)
}

const showcaseBarcode = (showcase) => {
  const codes = Array.isArray(showcase?.barcode) ? showcase.barcode : []
  const firstCode = codes.find((code) => String(code || '').trim())
  return firstCode ? String(firstCode) : '—'
}

const editShowcaseBarcode = computed(() => {
  const codes = Array.isArray(editForm.value.barcode) ? editForm.value.barcode : []
  const firstCode = codes.find((code) => String(code || '').trim())
  return firstCode ? String(firstCode) : ''
})
const editShowcaseTotal = computed(() => {
  const products = (editForm.value.items || []).reduce((sum, item) => {
    return sum + (Number(item.quantity) || 0) * (Number(item.price) || 0)
  }, 0)

  return products + (Number(editForm.value.order_collection_rate) || 0)
})

const EAN13_LEFT_ODD = {
  '0': '0001101',
  '1': '0011001',
  '2': '0010011',
  '3': '0111101',
  '4': '0100011',
  '5': '0110001',
  '6': '0101111',
  '7': '0111011',
  '8': '0110111',
  '9': '0001011'
}

const EAN13_LEFT_EVEN = {
  '0': '0100111',
  '1': '0110011',
  '2': '0011011',
  '3': '0100001',
  '4': '0011101',
  '5': '0111001',
  '6': '0000101',
  '7': '0010001',
  '8': '0001001',
  '9': '0010111'
}

const EAN13_RIGHT = {
  '0': '1110010',
  '1': '1100110',
  '2': '1101100',
  '3': '1000010',
  '4': '1011100',
  '5': '1001110',
  '6': '1010000',
  '7': '1000100',
  '8': '1001000',
  '9': '1110100'
}

const EAN13_PARITY = {
  '0': 'OOOOOO',
  '1': 'OOEOEE',
  '2': 'OOEEOE',
  '3': 'OOEEEO',
  '4': 'OEOOEE',
  '5': 'OEEOOE',
  '6': 'OEEEOO',
  '7': 'OEOEOE',
  '8': 'OEOEEO',
  '9': 'OEEOEO'
}

const escapePdfText = (value) => String(value || '').replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)')
const toPdfAscii = (value) => String(value || '').replace(/[^\x20-\x7E]/g, '').trim()

const calculateEan13CheckDigit = (digits12) => {
  const numbers = digits12.split('').map(Number)
  const total = numbers.reduce((sum, digit, index) => sum + digit * (index % 2 === 0 ? 1 : 3), 0)
  return String((10 - (total % 10)) % 10)
}

const resolveEan13Barcode = (barcode) => {
  const digits = String(barcode || '').replace(/\D/g, '')
  if (digits.length === 12) {
    return digits + calculateEan13CheckDigit(digits)
  }
  return digits.length === 13 ? digits : ''
}

const buildEan13Pattern = (barcode) => {
  const digits = resolveEan13Barcode(barcode)
  if (!digits) return null

  const first = digits[0]
  const leftDigits = digits.slice(1, 7).split('')
  const rightDigits = digits.slice(7).split('')
  const parity = EAN13_PARITY[first]

  if (!parity) return null

  const leftPattern = leftDigits.map((digit, index) => {
    return parity[index] === 'E' ? EAN13_LEFT_EVEN[digit] : EAN13_LEFT_ODD[digit]
  }).join('')

  const rightPattern = rightDigits.map((digit) => EAN13_RIGHT[digit]).join('')

  return {
    digits,
    pattern: `101${leftPattern}01010${rightPattern}101`
  }
}

const downloadPdf = (fileName, content) => {
  const blob = new Blob([content], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}

const createBarcodePdf = ({ showcaseName, barcode }) => {
  const width = 595.28
  const height = 841.89
  const bars = buildEan13Pattern(barcode)
  const normalizedBarcode = bars?.digits || String(barcode || '').trim()
  const title = String(showcaseName || '').trim() || 'Витрина'
  const bigTitle = 'Штрих-код витрины'
  const lines = [
    'BT',
    '/F1 11 Tf',
    `50 ${height - 44} Td`,
    `(${escapePdfText('Штрих-код витрины')}) Tj`,
    'ET',
    'BT',
    '/F1 8 Tf',
    `50 ${height - 60} Td`,
    `(${escapePdfText(title)}) Tj`,
    'ET',
  ]

  if (bars) {
    const patternLength = bars.pattern.length
    const barcodeWidth = 132
    const barWidth = barcodeWidth / patternLength
    const startX = 116
    const baseY = 610
    const barHeight = 68
    const guardHeight = 74
    const digitY = baseY - 16
    const firstDigitX = startX - 18
    const leftDigitsX = startX + (3 * barWidth) + 3
    const rightDigitsX = startX + (50 * barWidth) + 3
    const arrowStartX = startX + barcodeWidth + 5
    const arrowCenterY = digitY + 1.5
    const guardIndexes = new Set([0, 2, 45, 47, 92, 94])

    lines.push('0 g')
    bars.pattern.split('').forEach((bit, index) => {
      if (bit !== '1') return
      const currentX = startX + index * barWidth
      const currentHeight = guardIndexes.has(index) ? guardHeight : barHeight
      lines.push(`${currentX.toFixed(2)} ${baseY.toFixed(2)} ${barWidth.toFixed(2)} ${currentHeight.toFixed(2)} re f`)
    })

    lines.push('BT')
    lines.push('/F1 14 Tf')
    lines.push(`${firstDigitX.toFixed(2)} ${digitY.toFixed(2)} Td`)
    lines.push(`(${bars.digits[0]}) Tj`)
    lines.push('ET')

    lines.push('BT')
    lines.push('/F1 12 Tf')
    lines.push(`${leftDigitsX.toFixed(2)} ${digitY.toFixed(2)} Td`)
    lines.push(`(${bars.digits.slice(1, 7)}) Tj`)
    lines.push('ET')

    lines.push('BT')
    lines.push('/F1 12 Tf')
    lines.push(`${rightDigitsX.toFixed(2)} ${digitY.toFixed(2)} Td`)
    lines.push(`(${bars.digits.slice(7)}) Tj`)
    lines.push('ET')

    lines.push(`${arrowStartX.toFixed(2)} ${(arrowCenterY + 3.5).toFixed(2)} m ${(arrowStartX + 7).toFixed(2)} ${arrowCenterY.toFixed(2)} l ${(arrowStartX).toFixed(2)} ${(arrowCenterY - 3.5).toFixed(2)} l S`)
  } else {
    lines.push('BT')
    lines.push('/F1 18 Tf')
    lines.push(`50 ${height - 160} Td`)
    lines.push(`(${escapePdfText('Неподдерживаемый формат штрих-кода')}) Tj`)
    lines.push('ET')

    lines.push('BT')
    lines.push('/F1 28 Tf')
    lines.push(`50 ${height - 220} Td`)
    lines.push(`(${escapePdfText(normalizedBarcode || 'Нет штрих-кода')}) Tj`)
    lines.push('ET')
  }

  lines.push('BT')
  lines.push('/F1 7 Tf')
  lines.push(`50 42 Td`)
  lines.push(`(${escapePdfText(`Код: ${normalizedBarcode || 'Не назначен'}`)}) Tj`)
  lines.push('ET')

  const stream = `${lines.join('\n')}\n`
  const objects = []

  objects.push('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj')
  objects.push('2 0 obj\n<< /Type /Pages /Count 1 /Kids [3 0 R] >>\nendobj')
  objects.push(`3 0 obj
<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${width} ${height}] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>
endobj`)
  objects.push('4 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj')
  objects.push(`5 0 obj
<< /Length ${stream.length} >>
stream
${stream}endstream
endobj`)

  let pdf = '%PDF-1.4\n'
  const offsets = [0]

  objects.forEach((object) => {
    offsets.push(pdf.length)
    pdf += `${object}\n`
  })

  const xrefStart = pdf.length
  pdf += `xref
0 ${objects.length + 1}
0000000000 65535 f 
`

  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`
  })

  pdf += `trailer
<< /Size ${objects.length + 1} /Root 1 0 R >>
startxref
${xrefStart}
%%EOF`

  return pdf
}

const exportShowcaseBarcodePdf = async () => {
  if (!editShowcaseBarcode.value) return

  try {
    await exportBarcodePdfLabel({
      heading: 'Штрих-код витрины',
      title: editForm.value.name || 'Витрина',
      barcode: editShowcaseBarcode.value,
      fileName: `Штрихкод витрины-${editForm.value.name || 'export'}.pdf`
    })
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось экспортировать PDF штрих-кода', life: 3000 })
  }
}

const fetchData = async () => {
  loading.value = true
  try {
    const [showcasesRes, storeRes] = await Promise.all([
      axios.get('/api/showcases', { params: { price_source: 'daily_only' } }),
      axios.get('/api/store', { params: { price_source: 'daily_only' } })
    ])

    showcases.value = showcasesRes.data || []
    storeItems.value = storeRes.data || []
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: error?.response?.data?.message || 'Не удалось загрузить витрины', life: 3000 })
  } finally {
    loading.value = false
  }
}

const deleteShowcase = async (id) => {
  try {
    await axios.delete(`/api/showcases/${id}`)
    showcases.value = showcases.value.filter((showcase) => showcase.id !== id)
    toast.add({ severity: 'success', summary: 'Удалено', detail: 'Витрина удалена', life: 2500 })
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: error?.response?.data?.message || 'Не удалось удалить витрину', life: 3000 })
  }
}

const saveItem = async (item) => {
  const amount = Number(item.amount || 0)
  const quantity = Number(item.quantity || 0)

  if (quantity > amount) {
    toast.add({ severity: 'warn', summary: 'Ошибка количества', detail: `Максимум доступно: ${amount}`, life: 2500 })
    item.quantity = amount > 0 ? amount : 1
    return
  }

  try {
    await axios.put(`/api/showcase-items/${item.id}`, { quantity })
    toast.add({ severity: 'success', summary: 'Сохранено', detail: 'Позиция обновлена', life: 2000 })
    await fetchData()
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: error?.response?.data?.message || 'Не удалось обновить позицию', life: 3000 })
  }
}

const deleteItem = async (itemId) => {
  try {
    await axios.delete(`/api/showcase-items/${itemId}`)
    toast.add({ severity: 'success', summary: 'Удалено', detail: 'Позиция удалена', life: 2000 })
    await fetchData()
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: error?.response?.data?.message || 'Не удалось удалить позицию', life: 3000 })
  }
}

const openEdit = (showcase) => {
  editForm.value = {
    id: showcase.id,
    name: showcase.name,
    barcode: Array.isArray(showcase.barcode) ? [...showcase.barcode] : [],
    image: showcase.image || '',
    order_collection_rate: showcase.order_collection_rate,
    items: (showcase.items || []).map((item) => ({ ...item }))
  }
  editImageFile.value = null
  editImagePreview.value = ''
  editSideTab.value = 'items'
  editStoreQuery.value = ''
  editDialog.value = true
}

const onEditImageSelect = (event) => {
  const file = event.files?.[0] ?? null
  if (!file) return
  editImageFile.value = file
  editImagePreview.value = URL.createObjectURL(file)
}

const clearEditImage = () => {
  editImageFile.value = null
  editImagePreview.value = ''
  editForm.value.image = ''
}

const openPreview = (showcase) => {
  previewImageSrc.value = showcase.image || ''
  previewImageAlt.value = showcase.name || 'Фото витрины'
  previewDialog.value = Boolean(showcase.image)
}

const generateShowcaseBarcode = async () => {
  if (!editForm.value.id || editShowcaseBarcode.value) return

  generatingBarcode.value = true
  try {
    const { data } = await axios.post(`/api/showcases/${editForm.value.id}/barcode`)
    editForm.value.barcode = Array.isArray(data?.barcode) ? data.barcode : []

    const showcaseIndex = showcases.value.findIndex((item) => item.id === editForm.value.id)
    if (showcaseIndex !== -1) {
      showcases.value[showcaseIndex] = {
        ...showcases.value[showcaseIndex],
        barcode: [...editForm.value.barcode],
      }
    }

    toast.add({ severity: 'success', summary: 'Успех', detail: 'Штрихкод сгенерирован', life: 2500 })
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: error?.response?.data?.message || 'Не удалось сгенерировать штрихкод', life: 3000 })
  } finally {
    generatingBarcode.value = false
  }
}

const addStoreToEdit = async (store) => {
  if (!editForm.value.id) return

  const amount = Number(store.amount || 0)
  if (amount <= 0) {
    toast.add({ severity: 'warn', summary: 'Нет остатка', detail: 'Товар закончился на складе', life: 2500 })
    return
  }

  try {
    await axios.post(`/api/showcases/${editForm.value.id}/items`, {
      store_id: store.id,
      quantity: 1
    })
    await fetchData()

    const fresh = showcases.value.find((s) => s.id === editForm.value.id)
    if (fresh) editForm.value.items = fresh.items || []

    toast.add({ severity: 'success', summary: 'Успех', detail: 'Товар добавлен в витрину', life: 2000 })
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: error?.response?.data?.message || 'Не удалось добавить товар', life: 3000 })
  }
}

const clampEditItemQuantity = (item) => {
  const max = Number(item.amount || 0)
  const safeMax = max > 0 ? max : 1
  const current = Number(item.quantity || 1)
  item.quantity = Math.max(1, Math.min(current, safeMax))
}

const decrementEditItem = (item) => {
  item.quantity = Math.max(1, Number(item.quantity || 1) - 1)
  clampEditItemQuantity(item)
}

const incrementEditItem = (item) => {
  item.quantity = Number(item.quantity || 1) + 1
  clampEditItemQuantity(item)
}

const removeItemFromEdit = (itemId) => {
  editForm.value.items = (editForm.value.items || []).filter((item) => item.id !== itemId)
}

const updateShowcase = async () => {
  if (!editForm.value.id) return

  updating.value = true
  try {
    const hasOverflow = (editForm.value.items || []).some((item) => Number(item.quantity || 0) > Number(item.amount || 0))
    if (hasOverflow) {
      toast.add({ severity: 'warn', summary: 'Ошибка количества', detail: 'Количество не может быть больше остатка', life: 3000 })
      updating.value = false
      return
    }

    const formData = new FormData()
    formData.append('_method', 'PUT')
    formData.append('name', editForm.value.name || '')
    formData.append('order_collection_rate', String(editForm.value.order_collection_rate || 0))

    ;(editForm.value.items || []).forEach((item, index) => {
      formData.append(`items[${index}][store_id]`, String(item.store_id))
      formData.append(`items[${index}][quantity]`, String(item.quantity || 1))
    })

    if (editImageFile.value instanceof File) {
      formData.append('image', editImageFile.value)
    }

    await axios.post(`/api/showcases/${editForm.value.id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    toast.add({ severity: 'success', summary: 'Успех', detail: 'Витрина обновлена', life: 2500 })
    editDialog.value = false
    editImageFile.value = null
    editImagePreview.value = ''
    await fetchData()
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: error?.response?.data?.message || 'Не удалось обновить витрину', life: 3000 })
  } finally {
    updating.value = false
  }
}

onMounted(fetchData)
</script>

<style scoped>
:deep(.p-card),
:deep(.p-card-body),
:deep(.p-card-content) {
  height: 100%;
}

:deep(.p-card-body),
:deep(.p-card-content) {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  flex-direction: column;
}
</style>
